import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import Loader from "@/components/atoms/Loader";
import IconButton from "@/components/atoms/IconButton";
import SoftBadge from "@/components/atoms/SoftBadge";
import { canManageMontageMeeting } from "@/helpers/MeetingHelpers";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { removeUndefined } from "@/helpers/utils";
import { PaginateCardProps } from "@/interfaces/Card";
import { CalendarType, CompanyMeetingFilters } from "@/interfaces/Meeting";
import {
  useDeleteCompanyMeetingMutation,
  useGetCompanyMeetingsQuery,
} from "@/services/companies/meetings";
import { selectCurrentUser } from "@/services/slices/authSlice";
import {
  faClock,
  faEllipsisVertical,
  faPlus,
  faUserPen,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { DateTime } from "luxon";
import { ElementRef, FC, useEffect, useRef, useState } from "react";
import { Col, Dropdown, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast, ToastContentProps } from "react-toastify";
import AreYouSurToContinueModal from "../../modals/AreYouSurToContinueModal";
import UpdateCompanyMeetingModal from "../../modals/company/UpdateCompanyMeetingModal";
import StoreCompanyMeetingModal from "../../modals/company/StoreCompanyMeetingModal";
import PaginateCard from "../PaginateCard";

const CompanyMeetingsCard: FC<
  PaginateCardProps & { companyId: string; calendarType: CalendarType }
> = ({ companyId, calendarType, onGetTotal }) => {
  const user = useSelector(selectCurrentUser);
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const UpdateCompanyMeetingModalRef =
    useRef<ElementRef<typeof UpdateCompanyMeetingModal>>(null);
  const StoreCompanyMeetingModalRef =
    useRef<ElementRef<typeof StoreCompanyMeetingModal>>(null);
  const canCreateMontage =
    calendarType === "montage" &&
    checkIfUserHasRoles(user, ["ADMIN", "CSM", "AE"]);
  const canCreate = calendarType === "commercial" || canCreateMontage;
  const [deleteCompanyMeetingAsync] = useDeleteCompanyMeetingMutation();
  // `calendarType` fait partie des filtres et non d'un appel à part : la fiche
  // a un onglet par type, chacun ne demande que les siens, et la recherche et
  // la pagination de la carte s'appliquent au seul onglet ouvert.
  const [filters, setFilters] = useState<CompanyMeetingFilters>({
    pageIndex: 0,
    calendarType,
  });
  const { data, isLoading, error, isError } = useGetCompanyMeetingsQuery([
    companyId,
    filters,
  ]);

  const onDelete = async (id: string) => {
    toast.promise(deleteCompanyMeetingAsync([companyId, id]).unwrap(), {
      pending: "Suppression du rendez-vous en cours ...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              Le rendez-vous à bien été supprimé de l'entreprise 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  useEffect(() => {
    data?.meta && onGetTotal?.(data.meta.total);
  }, [data?.meta]);

  if (isLoading) {
    return <Loader />;
  } else if (isError) {
    return <ErrorComponent error={error} />;
  }

  return (
    <PaginateCard
      meta={data?.meta}
      filters={filters}
      setFilters={setFilters}
      headerActions={
        canCreate && (
          <IconButton
            variant="nolina-default"
            size="sm"
            icon={faPlus}
            onClick={() =>
              StoreCompanyMeetingModalRef.current?.showOrHiddenModal()
            }
          >
            Ajouter un rendez-vous {calendarType}
          </IconButton>
        )
      }
    >
      {canCreate && (
        <StoreCompanyMeetingModal
          ref={StoreCompanyMeetingModalRef}
          companyId={companyId}
          calendarType={calendarType}
        />
      )}
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Êtes vous sûr de vouloir détacher l'utilisateur qui à pour id : {{id}}"
      />
      <UpdateCompanyMeetingModal
        ref={UpdateCompanyMeetingModalRef}
        companyId={companyId}
      />
      {data?.data.map(
        ({
          id,
          startAt,
          createdAt,
          company,
          calendarType,
          authorId,
          author,
          csmOwnerId,
          canManage,
          montageStatus,
        }) => {
          // Le drapeau vient de l'API, qui seule connaît la liaison commerciale.
          // Repli sur l'ancien calcul tant qu'une API plus ancienne peut être en
          // ligne : l'API et le Dashboard se déploient séparément.
          const canManageMontage =
            canManage ??
            canManageMontageMeeting(user, { authorId, csmOwnerId });
          const canEdit = calendarType !== "montage" || canManageMontage;
          const canDelete =
            calendarType === "montage"
              ? canManageMontage
              : checkIfUserHasRoles(user, ["ADMIN", "AE", "CSM"]);

          return (
            <div
              key={id}
              className="bg-white dark__bg-1100 p-x1 rounded-3 shadow-sm"
            >
              <Row className="flex-between-center">
                <Col xs={10}>
                  <p className="fw-semi-bold mb-0">
                    {removeUndefined(
                      `${company?.name} / ${company?.sdr?.user.firstname}`,
                    )}
                  </p>
                </Col>

                {(canEdit || canDelete) && (
                  <Col xs={"auto"} className="d-flex justify-content-end">
                    <Dropdown
                      align="end"
                      className="btn-reveal-trigger d-inline-block"
                    >
                      <Dropdown.Toggle split variant="nolina-default" size="sm">
                        <FontAwesomeIcon
                          icon={faEllipsisVertical}
                          className="fs--2"
                        />
                      </Dropdown.Toggle>

                      <Dropdown.Menu className="border py-0">
                        <div className="py-2">
                          {canEdit && (
                            <Dropdown.Item
                              onClick={() =>
                                UpdateCompanyMeetingModalRef.current?.showOrHiddenModal(
                                  id,
                                )
                              }
                            >
                              Modifier
                            </Dropdown.Item>
                          )}
                          {canDelete && (
                            <Dropdown.Item
                              className="text-danger"
                              onClick={() =>
                                DeleteModalRef.current?.showOrHiddenModal(id)
                              }
                            >
                              Supprimer
                            </Dropdown.Item>
                          )}
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                )}
              </Row>
              <div className="border-bottom border-lg-0 border-0 my-3"></div>
              <Row className="flex-between-center">
                <Col xs={12} lg={"auto"}>
                  <div className="border-bottom border-lg-0 border-0 my-lg-0 my-3"></div>
                  <Flex direction="column">
                    <div className="d-flex flex-wrap gap-2">
                      <SoftBadge
                        bg={company?.status?.backgroundColor}
                        textColor={company?.status?.textColor}
                      >
                        {company?.status?.name ?? "Inconnu"}
                      </SoftBadge>
                      {/* Le statut du rendez-vous montage vient s'ajouter à
                          celui de l'entreprise, il ne le remplace pas : les
                          deux répondent à des questions différentes. */}
                      {calendarType === "montage" && montageStatus && (
                        <SoftBadge
                          bg={montageStatus.backgroundColor}
                          textColor={montageStatus.textColor}
                        >
                          {montageStatus.name}
                        </SoftBadge>
                      )}
                    </div>
                    <h6 className="d-flex align-items-center gap-1 mb-0 mt-2">
                      <FontAwesomeIcon
                        icon={faClock}
                        transform="shrink-3 up-1"
                      />
                      <span className="text-600">
                        {DateTime.fromISO(startAt)
                          .setLocale("fr")
                          .toFormat("dd LLL yyyy HH:mm")}
                      </span>
                    </h6>
                    {/* Styled like the date line but kept a <p>: the card title
                        already names the company's SDR, so this line spells out
                        "Créé le" rather than relying on a bare person icon — and
                        it leads with the creation date because meetings recorded
                        before authors were tracked have no author at all. */}
                    <p className="h6 d-flex align-items-center gap-1 mb-0 mt-2">
                      <FontAwesomeIcon
                        icon={faUserPen}
                        transform="shrink-3 up-1"
                      />
                      <span className="text-600">
                        {`Créé le ${DateTime.fromISO(createdAt)
                          .setLocale("fr")
                          .toFormat("dd LLL yyyy")}${
                          author ? ` par ${author.fullname}` : ""
                        }`}
                      </span>
                    </p>
                  </Flex>
                </Col>
              </Row>
            </div>
          );
        },
      )}
    </PaginateCard>
  );
};

export default CompanyMeetingsCard;
